/**
 * PORT (wide screens): two text controls of the original that a finger could not hit on a phone.
 *  - The roulette's bets (menu_roul: bet_gold_500 / 3000 / 10000, bet_diam_5 / 25 / 50): text fields that take the
 *    click themselves (new_interf.click "bet"), 96x12 CSS px each and 18 CSS px apart on a phone, and one tap places
 *    the bet at once. With a wide host each bet takes the taps of its whole row: from its text to the right end of the
 *    odds in that row, split half way to the rows above and below (a transparent pad named as the field, under it: the
 *    game's own click on the bet). A diamond bet on a touch screen asks first (src/main.ts holdTap, `betAt`).
 *  - The dropdown filters of the shop's weapons (filt: shop_mode_<id> texts, shop_set_<id> buttons whose only art is
 *    their hit area) and of the skills (perk_mode_<id> / perk_set_<id>): 19 rows of 8 CSS px text 11.6 CSS px apart in
 *    a narrow list. With a wide host where a row measures less than ROW_CSS, the open list is laid out in columns
 *    over the screen under the filter's header (within the shown safe area, over the margin beside the window where the
 *    columns need the room: a 16:9 phone): rows ROW_CSS apart, their text about TEXT_CSS (never less than
 *    TEXT_MIN_CSS, never below the original's), each row's button over its whole cell (its hit area its only art: the
 *    cell takes the taps, its pressed look lies on the cell), on a white panel of the port's own (the original's
 *    narrow one hidden), the filter on top of its screen's other children meanwhile (the panel reaches over the
 *    screen beside it: the perks' description and hide button). The game keeps picking the filter by its buttons, and writes
 *    the rows' texts at each render; the layout follows.
 * Without a wide host everything is the original's.
 */
import { Point, Sprite, TextFormat } from '@flash';

/** A rectangle in stage coordinates. */
interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A bet's field of the roulette (its currency and amount: new_interf's click splits the name the same way). */
export const BET_FIELD = /^bet_(gold|diam)_(\d+)$/;
/** The bets' pads (their names are the fields' own: the game's click reads the target's name). */
const BET_PAD_TAG = '$wideBetPad';
/** Room kept above the first bet and below the last one (stage px), and left of a bet's text. */
const BET_EDGE = 6;

/** What a filter's row measures at least (CSS px), its text (CSS px) and the smallest text a long row gets. */
export const ROW_CSS = 44;
const TEXT_CSS = 12.5;
const TEXT_MIN_CSS = 10.5;
/** Room inside the panel: round it, and left and right of a row's text (CSS px). */
const PAD_CSS = 4;
const TEXT_PAD_CSS = 10;
/** Room kept from the shown safe area's edges (CSS px). */
const EDGE_CSS = 6;
/** A filter's field and its button. */
const FILTER_ROW = /^(shop|perk)_mode_(.+)$/;
/** The panel's colours: white, a thin border and the rows' separators. */
const PANEL = { fill: 0xffffff, border: 0x9c9273, line: 0xe8e3d2 };

/** The size the game writes a field's text in (its default format; a field it never set: the text's own). */
function gameSize(tf: any): number {
  const d = Number(tf.defaultTextFormat?.size);
  return d > 0 ? d : Number(tf.getTextFormat().size) || 12;
}

function setSize(tf: any, px: number): void {
  if (tf.getTextFormat().size === px) return;
  const f = new TextFormat();
  f.size = px;
  tf.setTextFormat(f);
}

/** A bet's currency and amount (null: not a bet's field or pad). */
export function betOf(target: any): { currency: 'gold' | 'diam'; amount: number; field: any } | null {
  for (let o = target; o; o = o.parent) {
    const m = typeof o.name === 'string' ? BET_FIELD.exec(o.name) : null;
    if (!m) continue;
    const parent = o.parent;
    let field: any = null;
    if (parent && typeof parent.numChildren === 'number') {
      for (let i = 0; i < parent.numChildren; i++) {
        const c = parent.getChildAt(i);
        if (c.name === o.name && typeof c.text === 'string') field = c;
      }
    }
    return { currency: m[1] as 'gold' | 'diam', amount: Number(m[2]), field };
  }
  return null;
}

/** A field's or a button's place as the original has it. */
interface Orig {
  x: number;
  y: number;
  sx: number;
  sy: number;
  width: number | null;
  hit: any;
  index: number;
}

interface FilterState {
  /** The filter's clip to the window as the wide screen dressed it. */
  clip: boolean;
  /** The rows' fields and buttons (the open list's instances) and what they had from the game. */
  orig: Map<any, Orig>;
  /** The list's own panel (hidden: the original's narrow list) and the port's. */
  hidden: any[];
  panel: Sprite;
  key: string;
  /** The filter's place among its screen's children (it lies on top of them while its list is laid out). */
  index: number;
}

export class WideFingers {
  private readonly filters = new Map<any, FilterState>();
  private readonly pads = new Map<any, Sprite>();

  /** @param stage the stage (its cssScale, safeRect) */
  constructor(private readonly stage: any) {}

  /** The screens shown (the game's stack, not the popups' dim): their bets and filters for a wide host (`on`). */
  update(screens: any[], on: boolean): void {
    const css = Number(this.stage.cssScale);
    const live = new Set<any>();
    const livePads = new Set<any>();
    if (on && css > 0) {
      for (const link of screens) {
        if (!link || !link.stage) continue;
        this.betPads(link, livePads);
        const filt = link.filt;
        if (filt && filt.stage && filt.visible) {
          if (this.layoutFilter(filt, css)) live.add(filt);
        }
      }
    }
    for (const [filt, st] of this.filters) {
      if (live.has(filt)) continue;
      this.restoreFilter(filt, st);
      this.filters.delete(filt);
    }
    for (const [field, pad] of this.pads) {
      if (livePads.has(field)) continue;
      if (pad.parent) pad.parent.removeChild(pad);
      this.pads.delete(field);
    }
  }

  // ------------------------------------------------------------------------------------------------ the bets

  private betPads(link: any, live: Set<any>): void {
    const bets: any[] = [];
    const fields: Area[] = [];
    for (let i = 0; i < link.numChildren; i++) {
      const c = link.getChildAt(i);
      if (typeof c.text !== 'string') continue;
      if (BET_FIELD.test(String(c.name)) && c.visible) bets.push(c);
      else if (c.visible) fields.push(c.getBounds(link));
    }
    if (!bets.length) return;
    // (left to right: a row's odds are taken one after another)
    fields.sort((p, q) => p.x - q.x);
    const boxes = bets.map((b) => ({ b, r: b.getBounds(link) as Area })).sort((p, q) => p.r.y - q.r.y);
    boxes.forEach(({ b, r }, k) => {
      const prev = boxes[k - 1]?.r;
      const next = boxes[k + 1]?.r;
      const y0 = prev ? (prev.y + prev.height + r.y) / 2 : r.y - BET_EDGE;
      const y1 = next ? (r.y + r.height + next.y) / 2 : r.y + r.height + BET_EDGE;
      // To the right end of the row's odds: the fields whose middle lies in the bet's band, right of it.
      let x1 = r.x + r.width;
      for (const f of fields) {
        const cy = f.y + f.height / 2;
        if (cy > y0 && cy < y1 && f.x >= r.x + r.width - 2 && f.x - x1 < 120) x1 = Math.max(x1, f.x + f.width);
      }
      let pad = this.pads.get(b);
      if (!pad) {
        pad = new Sprite();
        (pad as any)[BET_PAD_TAG] = true;
        pad.buttonMode = true;
        pad.mouseChildren = false;
        this.pads.set(b, pad);
      }
      pad.name = b.name;
      // (drawn again only when its row moved: a redraw of the screen each frame costs a phone)
      const key = [r.x - BET_EDGE, y0, x1, y1].map((v) => Math.round(v * 10)).join(',');
      if ((pad as any).$key !== key) {
        (pad as any).$key = key;
        const g = pad.graphics;
        g.clear();
        g.beginFill(0, 0);
        g.drawRect(r.x - BET_EDGE, y0, x1 - (r.x - BET_EDGE), y1 - y0);
        g.endFill();
      }
      // (right under its field: the field keeps its own taps)
      if (pad.parent !== link) link.addChildAt(pad, link.getChildIndex(b));
      else if (link.getChildIndex(pad) > link.getChildIndex(b)) link.setChildIndex(pad, Math.max(0, link.getChildIndex(b) - 1));
      live.add(b);
    });
  }

  // ------------------------------------------------------------------------------------------------ the filters

  /** Lays the open list of `filt` out in finger rows; false when it is not open (or needs no layout). */
  private layoutFilter(filt: any, css: number): boolean {
    const rows: Array<{ tf: any; btn: any }> = [];
    for (let i = 0; i < filt.numChildren; i++) {
      const c = filt.getChildAt(i);
      const m = typeof c.text === 'string' ? FILTER_ROW.exec(String(c.name)) : null;
      if (!m || !c.visible) continue;
      const btn = filt.getChildByName(`${m[1]}_set_${m[2]}`);
      if (btn && btn.visible) rows.push({ tf: c, btn });
    }
    const head = filt.cur_filt;
    if (rows.length < 2 || !head) return false;
    let st = this.filters.get(filt);
    // (another open list: the timeline made its rows anew)
    if (st && !rows.every((r) => st!.orig.has(r.tf))) {
      this.restoreFilter(filt, st);
      this.filters.delete(filt);
      st = undefined;
    }
    if (!st) {
      const orig = new Map<any, Orig>();
      for (const { tf, btn } of rows) {
        orig.set(tf, { x: tf.x, y: tf.y, sx: tf.scaleX, sy: tf.scaleY, width: tf.width / (tf.scaleX || 1), hit: null, index: filt.getChildIndex(tf) });
        orig.set(btn, { x: btn.x, y: btn.y, sx: btn.scaleX, sy: btn.scaleY, width: null, hit: btn.hitTestState, index: filt.getChildIndex(btn) });
      }
      // The original's list panel: a shape of the open list that starts below its header.
      const hb = head.getBounds(filt);
      const hidden: any[] = [];
      for (let i = 0; i < filt.numChildren; i++) {
        const c = filt.getChildAt(i);
        if (c.$renderKind === 'text' || c.$isButton || orig.has(c) || (typeof c.name === 'string' && c.name.startsWith('$'))) continue;
        const b = c.getBounds(filt);
        if (b.y >= hb.y + hb.height / 2 && b.height > 3 * hb.height && c.visible) hidden.push(c);
      }
      const panel = new Sprite();
      panel.name = '$wide_filter_panel';
      // (it takes the taps between the rows: nothing under the open list is pressed through it)
      panel.mouseChildren = false;
      filt.addChildAt(panel, 0);
      st = { orig, hidden, panel, key: '', clip: !!filt.$designClip, index: filt.parent ? filt.parent.getChildIndex(filt) : -1 };
      this.filters.set(filt, st);
    }
    const original = st.orig.get(rows[0].tf)!;
    // The original's step between two rows (px of the stage).
    const ys = rows.map((r) => st!.orig.get(r.tf)!.y).sort((a, b) => a - b);
    let step = Infinity;
    for (let i = 1; i < ys.length; i++) if (ys[i] - ys[i - 1] > 0.5) step = Math.min(step, ys[i] - ys[i - 1]);
    const m = filt.transform.concatenatedMatrix;
    const S = Math.abs(m.a) || 1;
    if (!Number.isFinite(step) || step * S * css >= ROW_CSS) {
      this.restoreFilter(filt, st);
      this.filters.delete(filt);
      return false;
    }
    void original;
    // On top of its screen's other children while its list is laid out: the panel reaches over the screen beside the
    // filter (the skills' perks: their description, the page number, the hide button's area) and takes every tap of
    // its cells.
    const link = filt.parent;
    if (link && link.getChildIndex(filt) !== link.numChildren - 1) link.setChildIndex(filt, link.numChildren - 1);
    // Everything below in stage px, placed through the filter's own coordinates; laid out again when what it depends
    // on changed (the game's render writes the same texts each time).
    const safe = this.stage.safeRect as Area;
    const key = `${css}|${safe.x},${safe.y},${safe.width},${safe.height}|${rows.map((r) => `${r.tf.name}:${String(r.tf.text)}`).join(';')}|${S}|${filt.x},${filt.y}`;
    if (key === st.key) return true;
    st.key = key;
    const hb = head.getBounds(this.stage);
    let left = hb.x;
    for (let i = 0; i < filt.numChildren; i++) {
      const c = filt.getChildAt(i);
      if (c.$renderKind !== 'text' || st.orig.has(c) || !c.visible) continue;
      const b = c.getBounds(this.stage);
      if (Math.abs(b.y + b.height / 2 - (hb.y + hb.height / 2)) < hb.height) left = Math.min(left, b.x);
    }
    const edge = EDGE_CSS / css;
    const pad = PAD_CSS / css;
    const textPad = TEXT_PAD_CSS / css;
    const top = hb.y + hb.height + 2 / css;
    const bottom = safe.y + safe.height - edge;
    // (over the margin beside the window too where the columns need it: the filter is not clipped to the window while
    // its list is open, ui/wide-screen.ts $designClip)
    const right = safe.x + safe.width - edge;
    const p = ROW_CSS / css;
    const n = rows.length;
    const fit = Math.max(1, Math.floor((bottom - top - 2 * pad) / p));
    const cols = Math.ceil(n / fit);
    const per = Math.ceil(n / cols);
    // The rows' text: TEXT_CSS where the columns fit, else smaller down to TEXT_MIN_CSS (never below the original's).
    // (measured at the size the game writes them in: its narrow list had made a long one smaller, unsqueezeText)
    let widest = 0;
    for (const { tf } of rows) {
      setSize(tf, gameSize(tf));
      widest = Math.max(widest, tf.textWidth + 4);
    }
    const o0 = st.orig.get(rows[0].tf)!;
    // (k: the fields' scale over their own, for text of `cssPx` CSS px at the size the game writes it in)
    const gs = gameSize(rows[0].tf);
    const kOf = (cssPx: number) => Math.max(1, cssPx / (gs * o0.sy * S * css));
    let k = kOf(TEXT_CSS);
    const room = Math.max(1, right - left);
    const textRoom = room / cols - 2 * textPad;
    if (widest * k * o0.sy * S > textRoom) k = Math.max(kOf(TEXT_MIN_CSS), textRoom / (widest * o0.sy * S));
    const colW = Math.min(room / cols, widest * k * o0.sy * S + 2 * textPad);
    const panelArea: Area = { x: left, y: top, width: cols * colW, height: per * p + 2 * pad };
    for (const c of st.hidden) c.visible = false;
    filt.$designClip = false;
    // The panel (the filter's coordinates).
    const local = (x: number, y: number) => filt.globalToLocal(new Point(x, y));
    const g = st.panel.graphics;
    g.clear();
    const a0 = local(panelArea.x, panelArea.y);
    const a1 = local(panelArea.x + panelArea.width, panelArea.y + panelArea.height);
    g.lineStyle(1 / (css * S), PANEL.border, 1);
    g.beginFill(PANEL.fill, 1);
    g.drawRect(a0.x, a0.y, a1.x - a0.x, a1.y - a0.y);
    g.endFill();
    g.lineStyle(1 / (css * S), PANEL.line, 1);
    for (let c = 0; c < cols; c++) {
      const x0 = panelArea.x + c * colW;
      if (c > 0) {
        const q0 = local(x0, panelArea.y + pad);
        const q1 = local(x0, panelArea.y + panelArea.height - pad);
        g.moveTo(q0.x, q0.y);
        g.lineTo(q1.x, q1.y);
      }
      for (let r = 1; r < per && c * per + r < n; r++) {
        const y = panelArea.y + pad + r * p;
        const q0 = local(x0 + textPad / 2, y);
        const q1 = local(x0 + colW - textPad / 2, y);
        g.moveTo(q0.x, q0.y);
        g.lineTo(q1.x, q1.y);
      }
    }
    // The rows in the columns, in the original's order (top to bottom), each button's hit area its whole cell.
    const order = [...rows].sort((a, b) => st!.orig.get(a.tf)!.y - st!.orig.get(b.tf)!.y);
    order.forEach(({ tf, btn }, i) => {
      const c = Math.floor(i / per);
      const r = i % per;
      const cell: Area = { x: panelArea.x + c * colW, y: panelArea.y + pad + r * p, width: colW, height: p };
      const o = st!.orig.get(tf)!;
      tf.scaleX = tf.scaleY = o.sy * k;
      tf.width = Math.max(10, (colW - 2 * textPad) / (o.sy * k * S));
      // (a text still too long at the smallest scale: a smaller font, as the game's own render would give it)
      for (let px = gameSize(tf); tf.textWidth > tf.width / (tf.scaleX || 1) - 4 && px > 9; ) setSize(tf, --px);
      const tl = local(cell.x + textPad, cell.y + (p - (tf.textHeight + 4) * o.sy * k * S) / 2);
      tf.x = tl.x;
      tf.y = tl.y;
      // The button itself over the cell: its hit area (its only art but for the pressed look) then is the cell, and
      // its pressed look lies on the cell too. (Its art then measures a finger's target: the tab screens' hit areas
      // leave it alone, ui/wide-popups.ts setHits.)
      const ob = st!.orig.get(btn)!;
      const hs = ob.hit;
      const hb = hs && typeof hs.getBounds === 'function' ? hs.getBounds(hs) : null;
      if (!hb || !(hb.width > 0 && hb.height > 0)) return;
      const c0 = local(cell.x, cell.y);
      const c1 = local(cell.x + cell.width, cell.y + cell.height);
      btn.scaleX = (c1.x - c0.x) / hb.width;
      btn.scaleY = (c1.y - c0.y) / hb.height;
      btn.x = c0.x - hb.x * btn.scaleX;
      btn.y = c0.y - hb.y * btn.scaleY;
    });
    // The rows' buttons over the filter's own controls (the header's arrow, whose hit area grows over the panel's
    // first row on a phone): a tap in a cell is that cell's.
    for (const { btn } of order) filt.setChildIndex(btn, filt.numChildren - 1);
    st.panel.visible = true;
    return true;
  }

  private restoreFilter(filt: any, st: FilterState): void {
    for (const [o, v] of [...st.orig].sort((a, b) => a[1].index - b[1].index)) {
      o.scaleX = v.sx;
      o.scaleY = v.sy;
      o.x = v.x;
      o.y = v.y;
      if (v.width !== null) o.width = v.width;
      if (o.parent === filt && v.index < filt.numChildren) filt.setChildIndex(o, v.index);
    }
    for (const c of st.hidden) c.visible = true;
    const link = filt.parent;
    if (link && st.index >= 0 && st.index < link.numChildren && link.getChildIndex(filt) !== st.index) link.setChildIndex(filt, st.index);
    filt.$designClip = st.clip;
    if (st.panel.parent) st.panel.parent.removeChild(st.panel);
    void filt;
  }
}
